import { useMemo } from 'react'
import { useAuth } from './useAuth'
import { useUserEventInterests } from './useUserEventInterests'
import { useSocialEventData } from './useSocialEventData'
import { useFriendEventInterests } from './useFriendEventInterests'
import { useEventsByIds } from './useEventsByIds'
import { useHomeRegion } from './useHomeRegion'
import { useEventRegions } from './useEventRegions'
import { useBlockedUsers } from '../contexts/BlockedUsersContext'
import { isEventActive } from '../lib/formatDate'
import { getNow } from '../lib/clock'
import { isNearby } from '@shared/regions'
import { buildCrewActivity, millis } from '../lib/crewActivity'
import type { Event } from '../types'

export type { CrewActivity } from '../lib/crewActivity'

const WINDOW_MS = 30 * 24 * 60 * 60 * 1000
const MAX_ITEMS = 100

/**
 * What the viewer's crew did lately, newest first: friends and group mates who
 * marked going / interested on an upcoming event, and events linked to the
 * viewer's groups — one item per event (going, interested and group links
 * merged), so a crew in 2 groups sees an event once. Only the
 * last 30 days; RSVPs need `updatedAt`, which interests carry since the feed
 * shipped. Ticket items aren't here — they wait on the viewer, so they live in
 * Crew's inbox (`useCrewTicketActions`).
 *
 * Scoped to the viewer's region like Home's friends' plans (`isNearby`), except
 * events the viewer is going to or interested in.
 */
export function useCrewActivity() {
  const { signedInUid } = useAuth()
  const { eventStatuses: myStatuses } = useUserEventInterests(signedInUid)
  const social = useSocialEventData(myStatuses)
  const { rows, loading } = useFriendEventInterests(social.friendIds)
  const { isBlocked } = useBlockedUsers()

  const { recentRsvps, recentLinks, eventIds } = useMemo(() => {
    const since = getNow().getTime() - WINDOW_MS
    const recentRsvps = rows.filter(
      r => (r.status === 'going' || r.status === 'interested') && millis(r.updatedAt) >= since && !isBlocked(r.userId),
    )
    const recentLinks = social.allGroupEvents.filter(ge => millis(ge.createdAt) >= since && ge.createdBy !== signedInUid)
    const eventIds = [...new Set([...recentRsvps.map(r => r.eventId), ...recentLinks.map(ge => ge.eventId)])]
    return { recentRsvps, recentLinks, eventIds }
  }, [rows, social.allGroupEvents, signedInUid, isBlocked])
  const { events, pending } = useEventsByIds(eventIds)
  const { regionId, isEverywhere } = useHomeRegion()
  const { regions, loading: regionsLoading } = useEventRegions(events)

  const items = useMemo(() => {
    const now = getNow()
    const inScope = (e: Event) => {
      const mine = myStatuses.get(e.id)
      return isEverywhere || mine === 'going' || mine === 'interested' || isNearby(regionId, regions.get(e.id), e.type)
    }
    const byId = new Map(events.filter(e => isEventActive(e, now) && inScope(e)).map(e => [e.id, e]))
    const groupsById = new Map(social.groups.map(g => [g.id, g]))
    return buildCrewActivity({
      rsvps: recentRsvps,
      links: recentLinks,
      eventsById: byId,
      groupsById,
      viewerUid: signedInUid,
      max: MAX_ITEMS,
    })
  }, [events, recentRsvps, recentLinks, social.groups, signedInUid, myStatuses, isEverywhere, regionId, regions])

  return {
    items,
    profiles: social.mergedProfiles,
    myStatuses,
    loading: loading || (eventIds.length > 0 && (pending || regionsLoading)),
  }
}
